import { useEffect, useState } from "react";
import { API } from "../lib/apiClient";
import { Download, ExternalLink, Moon, Paperclip, Smartphone, Sun, X } from "lucide-react";

export function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("sigortacrm-theme") || "light"; } catch { return "light"; }
  });
  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-theme", theme);
      localStorage.setItem("sigortacrm-theme", theme);
    } catch {}
  }, [theme]);
  return (
    <button
      className="theme-toggle"
      data-testid="theme-toggle"
      onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
      title={theme === "dark" ? "Açık temaya geç" : "Karanlık temaya geç"}
    >
      {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

export function PdfPreviewModal({ preview, onClose }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("sigortapro-token") : "";
  let url;
  if (preview.url) {
    // Direct URL provided (e.g. quote PDFs). Ensure token param exists.
    url = /[?&]token=/.test(preview.url) ? preview.url : `${preview.url}${preview.url.includes("?") ? "&" : "?"}token=${encodeURIComponent(token || "")}`;
  } else {
    const base = `${API}/policies/${preview.policyId}/pdf?auth=${encodeURIComponent(token || "")}`;
    url = preview.attId ? `${base}&attId=${encodeURIComponent(preview.attId)}` : base;
  }
  const dlUrl = `${url}${url.includes("?") ? "&" : "?"}dl=1`;
  // iOS Safari does NOT inline-render PDFs via <iframe>. Detect and show a friendly fallback.
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  return (
    <div className="modal-backdrop" data-testid="pdf-preview-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: "min(1100px, 96vw)", width: "96vw", padding: 0, display: "flex", flexDirection: "column", height: "min(900px, 92vh)" }}>
        <div className="modal-head" style={{ padding: "14px 18px", borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ fontSize: 16, display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
            <Paperclip size={16} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {preview.filename}
              {preview.label && <span style={{ marginLeft: 10, fontSize: 12, color: "#64748b", fontWeight: 500 }}>· {preview.label}</span>}
            </span>
          </h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <a href={url} target="_blank" rel="noopener noreferrer" className="outline-button" data-testid="pdf-preview-open-tab" style={{ fontSize: 12, padding: "6px 10px" }} title="Yeni sekmede aç"><ExternalLink size={13} /> Sekmede aç</a>
            <a href={dlUrl} download={preview.filename} className="outline-button" data-testid="pdf-preview-download" style={{ fontSize: 12, padding: "6px 10px" }} title="İndir"><Download size={13} /> İndir</a>
            <button className="icon-button" data-testid="pdf-preview-close" onClick={onClose}><X size={18} /></button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: "#f1f5f9", position: "relative" }}>
          {isIOS ? (
            <div style={{ padding: 24, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 14 }}>
              <Paperclip size={44} color="#1a5fe0" />
              <strong style={{ fontSize: 15 }}>iOS Safari PDF önizlemeyi doğrudan göstermez</strong>
              <span style={{ fontSize: 13, color: "#64748b", maxWidth: 320 }}>Dosyayı yeni sekmede açmak için aşağıdaki butona basın.</span>
              <a href={url} target="_blank" rel="noopener noreferrer" className="primary-button" style={{ marginTop: 8 }}><ExternalLink size={15} /> PDF'i aç</a>
            </div>
          ) : (
            <iframe
              data-testid="pdf-preview-iframe"
              src={url}
              title={preview.filename}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Already installed?
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return;
    if (window.navigator.standalone) return; // iOS installed
    const dismissed = localStorage.getItem("sigortacrm-install-dismissed");
    if (dismissed === "1") return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    // iOS: no beforeinstallprompt — show manual tip after a delay
    const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
    const t = isIos ? setTimeout(() => setVisible(true), 3500) : null;
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      if (t) clearTimeout(t);
    };
  }, []);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") localStorage.setItem("sigortacrm-install-dismissed", "1");
      setDeferred(null);
      setVisible(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem("sigortacrm-install-dismissed", "1");
    setVisible(false);
  };

  if (!visible) return null;
  const isIos = /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !deferred;

  return (
    <div className="pwa-install-banner" data-testid="pwa-install-banner">
      <div className="pwa-install-icon"><Smartphone size={20} /></div>
      <div className="pwa-install-body">
        <strong>SigortaCrm'i ana ekrana ekle</strong>
        {isIos ? (
          <span>Safari'de <b>Paylaş</b> ➜ <b>Ana Ekrana Ekle</b> ile uygulama gibi kullanın.</span>
        ) : (
          <span>Uygulama gibi tek dokunuşla açın, offline poliçe listesine bakın.</span>
        )}
      </div>
      {!isIos && (
        <button className="primary-button" data-testid="pwa-install-btn" onClick={install}>Ekle</button>
      )}
      <button className="pwa-install-dismiss" data-testid="pwa-install-dismiss" onClick={dismiss} aria-label="Kapat"><X size={16} /></button>
    </div>
  );
}

